import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, XCircle, ScanLine } from "lucide-react";

export default function VerifyReport() {
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    company: params.get("company") || "",
    verified_count: "",
    total_co2_kg: "",
    total_units: "",
    generated_at: "",
    signature: "",
  });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const parseCanonical = (raw) => {
    // supports pasting: canonical = "Company|N|kg|units|iso"
    const m = raw.match(/canonical\s*=\s*"?([^"|]+)\|(\d+)\|([-0-9.]+)\|(\d+)\|([^"]+)"?/);
    if (m) setForm((f) => ({ ...f, company: m[1], verified_count: m[2], total_co2_kg: m[3], total_units: m[4], generated_at: m[5] }));
  };

  const verify = async () => {
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/verify-report", {
        company: form.company,
        verified_count: parseInt(form.verified_count || "0", 10),
        total_co2_kg: parseFloat(form.total_co2_kg || "0"),
        total_units: parseInt(form.total_units || "0", 10),
        generated_at: form.generated_at,
        signature: form.signature.trim(),
      });
      setResult(data);
    } catch (err) { setResult({ valid: false, error: err?.response?.data?.detail || "Failed" }); }
    finally { setBusy(false); }
  };

  return (
    <div className="mx-auto max-w-3xl px-6 py-16">
      <div className="mono text-xs text-primary">// public · report verifier</div>
      <h1 className="mt-2 text-5xl font-black tracking-tighter">Verify an impact report.</h1>
      <p className="mt-3 max-w-2xl text-muted-foreground">Paste the fields from any signed Eco Chamber report to confirm it hasn't been tampered with. Any changed number will fail verification.</p>

      <div className="mt-8 border border-border p-6 space-y-4">
        <Label className="text-xs uppercase tracking-widest text-muted-foreground">Paste canonical line (fastest)</Label>
        <Input data-testid="verify-canonical-paste" placeholder='canonical = "Acme Corp|1|0.0|0|2026-08-…"' onChange={(e) => parseCanonical(e.target.value)} className="rounded-none bg-input mono text-xs" />
        <div className="grid grid-cols-2 gap-4">
          <div><Label className="text-xs uppercase tracking-widest text-muted-foreground">Company</Label><Input data-testid="verify-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="mt-2 rounded-none bg-input" /></div>
          <div><Label className="text-xs uppercase tracking-widest text-muted-foreground">Verified count</Label><Input data-testid="verify-count" value={form.verified_count} onChange={(e) => setForm({ ...form, verified_count: e.target.value })} className="mt-2 rounded-none bg-input" /></div>
          <div><Label className="text-xs uppercase tracking-widest text-muted-foreground">Total CO₂ (kg)</Label><Input data-testid="verify-co2" value={form.total_co2_kg} onChange={(e) => setForm({ ...form, total_co2_kg: e.target.value })} className="mt-2 rounded-none bg-input" /></div>
          <div><Label className="text-xs uppercase tracking-widest text-muted-foreground">Total units</Label><Input data-testid="verify-units" value={form.total_units} onChange={(e) => setForm({ ...form, total_units: e.target.value })} className="mt-2 rounded-none bg-input" /></div>
        </div>
        <div><Label className="text-xs uppercase tracking-widest text-muted-foreground">Generated at (ISO)</Label><Input data-testid="verify-generated" value={form.generated_at} onChange={(e) => setForm({ ...form, generated_at: e.target.value })} className="mt-2 rounded-none bg-input mono" /></div>
        <div><Label className="text-xs uppercase tracking-widest text-muted-foreground">Signature (HMAC-SHA256)</Label><Input data-testid="verify-signature" value={form.signature} onChange={(e) => setForm({ ...form, signature: e.target.value })} className="mt-2 rounded-none bg-input mono text-xs" /></div>
        <Button data-testid="verify-submit" onClick={verify} disabled={busy} className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90"><ScanLine className="mr-2 h-4 w-4" />{busy ? "Checking…" : "Verify signature"}</Button>
      </div>

      {result && (
        <div data-testid="verify-result" className={`mt-6 border p-6 flex items-center gap-4 ${result.valid ? "border-primary bg-primary/10" : "border-destructive bg-destructive/10"}`}>
          {result.valid ? <ShieldCheck className="h-8 w-8 text-primary" /> : <XCircle className="h-8 w-8 text-destructive" />}
          <div>
            <div className="text-2xl font-black tracking-tighter">{result.valid ? "Signature valid" : "Signature invalid"}</div>
            <div className="mt-1 text-sm text-muted-foreground">{result.valid ? "This report was issued by Eco Chamber and none of the figures have been altered." : (result.error || "Signature does not match the report figures. The document may have been tampered with or copied incorrectly.")}</div>
          </div>
        </div>
      )}
    </div>
  );
}
